/* eslint-disable no-useless-escape,react/jsx-filename-extension,no-dupe-keys */
import React from 'react';
import sanitizeHtml from 'sanitize-html';
import striptags from 'striptags';
import htmlReactParser from 'html-react-parser/dist/html-react-parser';
import slugify from 'slugify';
import reactStringReplace from 'react-string-replace';
import API from '../config/API';
import TradeMap from "../components/Shortcodes/TradeMap";
import ExecutiveManagement from "../components/Shortcodes/ExecutiveManagement";

import {getVideoId} from "./video";
import PressReleasesRSS from "../components/PressReleasesRSS/PressReleasesRSS";
import JobRecruiterWidget from "../components/JobRecruiterWidget/JobRecruiterWidget";
import FormAssembly from "../components/FormAssembly/FormAssembly";
import VotionBracket from "../components/VotionBracket/VotionBracket";
import Jobs from "../components/Jobs/Jobs";
import VideoCallout from '../components/VideoCallout/VideoCallout';
import ReactVideoModalStandaloneButton
    from "../components/ReactVideoModalStandaloneButton/ReactVideoModalStandaloneButton";
import TwillContent from "../components/TwillContent/TwillContent";
import ReactVideoInline from "../components/ReactVideoInline/ReactVideoInline";
import JobSearch from "../components/Jobs/JobSearch";

export const isLandingPage = bundle => {
    if (!bundle) return false;

    return [
        'landing_page',
        'legal_page',
        'dealer_rankings_page',
        'post_trade_page',
        'mobile_optimized_page'
    ].includes(bundle);
};

export const isWhatsNewPage = bundle => {
    if (!bundle) return false;

    return ['what_s_new_page'].includes(bundle);
};

export const slugifyUrl = url => {
    const slugifyOptions = {
        replacement: '-',
        remove: /[*+~.()'"!:@/]/g,
        lower: true
    };

    return slugify(url, slugifyOptions);
};

export const removeNonAlphanumericChars = text => text.toLowerCase().replace(/\s+/gi, '_').replace(/[^0-9a-z_]/gi, '');

const apiHost = API.getHost();

// Rewrite Home route to / and handle Media
export const filterUrl = content => content
    .replace(API.home, '/')
    .replace('https://content.marketaxess.com/media', '/media')
    .replace('internal:/', '/');

export const filterUrlSocial = content => {
    if (content.startsWith('/')) {
        return `${window.location.origin}${content}`;
    }
    return content;
};

// Set absolute path for a resource to the API host.
export const filterResource = content => content.replace(/.*?\/sites\//, `${apiHost}/sites/`);

// Sanitize HTML for YITM Mobile Content
export const filterYitmMobileContent = content => sanitizeHtml(content, {
    allowedTags: [ 'b', 'i', 'em', 'strong', 'u', 'br', 'sup', 'span' ],
    textFilter: text => text.replace(/®|&reg;/g, '<sup>&reg;</sup>')
});

// Sanitize HTML for Headlines
export const filterHeadline = content => sanitizeHtml(content, {
    allowedStyles: {
        '*': {
            'color': [/^\#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/],
            'text-align': [/^left$/, /^right$/, /^center$/],
            'font-size': [/^\d+(?:px|em|%)$/]
        }
    },
    allowedAttributes: {
        '*': ['style']
    },
    allowedTags: [ 'b', 'i', 'em', 'strong', 'u', 'br', 'sup', 'span' ],
    textFilter: text => text.replace(/®|&reg;/g, '<sup>&reg;</sup>')
});

export const isInternalLink = link => {

    if (!link) {
        return true;
    }

    const extension = link.substr(link.lastIndexOf('.') + 1);

    // Mark PDFs as external links
    if (['pdf'].includes(extension)) {
        return false;
    }

    // Mark Media as external links
    if (link.includes('/media/')) {
        return false;
    }

    if (link.startsWith('/') || link.includes(window.location.hostname) || link.includes(apiHost)) {
        return true;
    }

    return false;
};

export const stripTagsModified = (content, options) => striptags(content.replace(/(&#8217;|&rsquo;|&lsquo;)/g, options, '\'').replace(/&reg;/g, '®').replace(/(&amp;|&#038;)/g, '&'));
export const stripTags = content => striptags(content.replace(/(&#8217;|&rsquo;|&lsquo;)/g, '\'').replace(/&reg;/g, '®').replace(/(&amp;|&#038;)/g, '&'));

export const filterHTML = (content, shortcodesEnabled=false, filterAnchorClasses=true, filterAnchorInlineStyles=true, injectShortcodeForVideoLinks=true) => {

    const contentReplaced = content
        .replace(/src="http:\/\/twcms.mktxwebsite.io\/storage\/uploads/g, 'src="https://assets.marketaxess.com/twu') // Automatically upgrade Twill Assets
        .replace(/href="https:\/\/content.marketaxess.com\/media\//g, 'href="/media/')
        .replace(/src="(.*?)"/g, (match, p1) => `src="${p1.replace(/.*?\/sites\//, `${apiHost}/sites/`)}"`)
        .replace(/href="(.*?)"/g, (match, p1) => `href="${p1.replace(/.*?\/sites\//, `${apiHost}/sites/`)}"`)
        .replace(/https:\/\/www.marketaxess.com\/sites\/default\/files\//g, 'https://assets.marketaxess.com/')
        .replace(/<a.*?href="(.*?)".*?>(.*?)<\/a>/g, (match, p1, p2) => {

            // Load classes when filterAnchorClasses is not set, primarily via TwillContent.jsx
            let additionalClasses = '';
            if (!filterAnchorClasses && match.includes('class=')) {
                const classMatch = match.match(/<a.*?class="(.*?)".*?>.*?<\/a>/);
                if (classMatch.length > 1) additionalClasses += classMatch.slice(-1)[0];
            }

            // Load classes when filterAnchorInlineStyles is not set, primarily via TwillContent.jsx
            let additionalInlineStyles = '';
            if (!filterAnchorInlineStyles && match.includes('style=')) {
                const styleMatch = match.match(/<a.*?style="(.*?)".*?>.*?<\/a>/);
                if (styleMatch.length > 1) additionalInlineStyles += styleMatch.slice(-1)[0];
            }

            const videoID = getVideoId(p1);
            const extension = p1.substr(p1.lastIndexOf('.') + 1);

            // Tag Videos for shortcode injection
            if (videoID && (!injectShortcodeForVideoLinks || !shortcodesEnabled) ) {
                return `<a class="video" target="_blank" rel="noopener noreferrer" href="${p1}">${p2}</a>`;
            } else if (videoID) {
                return `[[VideoModal uri="${p1}" text="${p2}"]]`;
            }

            if (isInternalLink(p1)) {
                // Localize page links
                if ((!extension || extension.endsWith('/')) && p1.includes(apiHost) && !p1.includes('/sites/')) {
                    return `<a class="${additionalClasses}" style="${additionalInlineStyles}" href="${p1.replace(apiHost, '')}">${p2}</a>`;
                }
                return `<a class="${additionalClasses}" style="${additionalInlineStyles}" href="${p1}">${p2}</a>`;
            }

            return `<a class="${additionalClasses}" style="${additionalInlineStyles}" target="_blank" rel="noopener noreferrer" href="${p1}">${p2}</a>`;
        })
        .replace(/®|&reg;/g, '<sup>&reg;</sup>');

    // Use alternative method to inject shortcodes.
    // Note: this can't use dangerouslySetInnerHTML and must be rendered directly such as <div>{filterHTML(myvar, true)}</div>
    if (shortcodesEnabled) {
        let shortcodeRendered = reactStringReplace(contentReplaced, /(\[\[CustomComponent component="TradeMap"]])/g, () => (
            <TradeMap/>
        ));

        shortcodeRendered = reactStringReplace(shortcodeRendered, /(\[\[CustomComponent component="PressReleases"]])/g, () => (
            <PressReleasesRSS/>
        ));

        shortcodeRendered = reactStringReplace(shortcodeRendered, /(\[\[CustomComponent component="JobRecruitersWidget"]])/g, () => (
            <JobRecruiterWidget/>
        ));

        shortcodeRendered = reactStringReplace(shortcodeRendered, /(\[\[VotionBracket(?:.*?)]])/g, (match) => {

            const srcMatch = match.match(/src="(.*?)"/);
            if (!(srcMatch && srcMatch[1])) return null;

            return (
                <VotionBracket src={srcMatch[1]}/>
            );
        });

        // Video Modal
        shortcodeRendered = reactStringReplace(shortcodeRendered, /(\[\[VideoModal(?:.*?)]])/g, (match) => {

            const uriMatch = match.match(/uri="(.*?)"/);
            if (!(uriMatch && uriMatch[1])) return null;

            const textMatch = match.match(/text="(.*?)"/);
            const text = textMatch && textMatch[1] ? textMatch[1] : null;

            return (
                <ReactVideoModalStandaloneButton uri={uriMatch[1]} text={text}/>
            );
        });

        // Inline Video
        shortcodeRendered = reactStringReplace(shortcodeRendered, /((?:<p>)?\[\[InlineVideo(?:.*?)]](?:<\/p>)?)/g, (match) => {

            const uriMatch = match.match(/uri="(.*?)"/);
            if (!(uriMatch && uriMatch[1])) return null;

            return (
                <ReactVideoInline uri={uriMatch[1]}/>
            );
        });

        // PathFactory Overlay Button Shortcode
        shortcodeRendered = reactStringReplace(shortcodeRendered, /(\[\[pathfactoryoverlaybutton(?:.*?)]]?)/g, match => {

            const textMatch = match.match(/text="(.*?)"/);
            if (!(textMatch && textMatch.length)) return null;
            const text = textMatch[textMatch.length-1];
            if (!text) return null;

            const hrefMatch = match.match(/href="(.*?)"/);
            if (!(hrefMatch && hrefMatch.length)) return null;
            const href = hrefMatch[hrefMatch.length-1];
            if (!href) return null;

            let className = 'text-cta';
            const classMatch = match.match(/class="(.*?)"/);
            if (classMatch && classMatch.length) {
                className = classMatch[classMatch.length-1];
            }

            return (
                // eslint-disable-next-line jsx-a11y/href-no-hash
                <a className={className} href="#" data-lookbook-overlay-href={href}>
                    {text}
                </a>
            );

        });

        // FormAssembly API Shortcode
        shortcodeRendered = reactStringReplace(shortcodeRendered, /((?:<p>)?\[\[formassembly(?:.*?)]](?:<\/p>)?)/g, (match) => {

            // Get the Form ID
            const idMatch = match.match(/id=(\w+)?/);
            if (!idMatch.length) return null;
            const id = idMatch[idMatch.length-1];
            if (!id) return null;

            // Styles
            const styleMatch = match.match(/style="(.*?)"/);
            let styles = '';
            if (styleMatch && styleMatch.length) {
                styles = styleMatch[styleMatch.length-1];
            }

            // Reference
            const referenceMatch = match.match(/reference="(.*?)"/);
            let reference = '';
            if (referenceMatch && referenceMatch.length) {
                reference = referenceMatch[referenceMatch.length-1];
            }

            const assemblyIdNumeric = parseInt(id, 10);

            return (
                <FormAssembly id={assemblyIdNumeric ? assemblyIdNumeric.toString() : id} styles={styles} reference={reference}/>
            );
        });

        // Twill CMS Shortcode
        shortcodeRendered = reactStringReplace(shortcodeRendered, /(\[\[twill-content(?:.*?)]])/g, (match) => {
            const pathMatch = match.match(/path="(.*?)"/);
            if (!(pathMatch && pathMatch.length)) return null;
            const path = pathMatch[pathMatch.length-1];
            if (!path) return null;

            return (
                <TwillContent path={path} />
            );
        });

        // Harvest Jobs API Shortcode
        // eslint-disable-next-line no-unused-vars,arrow-body-style
        shortcodeRendered = reactStringReplace(shortcodeRendered, /((?:<p>)?\[\[jobs(?:.*?)]](?:<\/p>)?)/g, (match) => {
            if(match.includes('location=')) {
                const location = match.split('location="')[1].split('"')[0];
                return (
                    <Jobs location={location} />
                );
            }

            if(match.includes('style=compact')) {
                return (
                    <JobSearch title="Search Jobs" />
                );
            }

            if(match.includes('category=')) {
                const category = match.split('category="')[1].split('"')[0];
                return (
                    <Jobs category={category} />
                );
            }

            if(match.includes('job_type=')) {
                const jobType = match.split('job_type="')[1].split('"')[0];
                return (
                    <Jobs jobType={jobType} />
                );
            }

            return (
                <Jobs/>
            );
        });


        // ExecutiveManagement
        shortcodeRendered = reactStringReplace(shortcodeRendered, /(\[\[executive-management-feed]])/g, () => <ExecutiveManagement/>);

        // Video Modal
        shortcodeRendered = reactStringReplace(shortcodeRendered, /(\[\[VideoCallout(?:.*?)]])/g, (match) => {

            const uriMatch = match.match(/uri="(.*?)"/);
            if (!(uriMatch && uriMatch[1])) return null;

            const textMatch = match.match(/text="(.*?)"/);
            const text = textMatch && textMatch[1] ? textMatch[1] : null;

            const thumbnailFileMatch = match.match(/thumbnailFile="(.*?)"/);
            const thumbnailFile = thumbnailFileMatch && thumbnailFileMatch[1] ? thumbnailFileMatch[1] : null;

            const thumbnailUrlMatch = match.match(/thumbnailUrl="(.*?)"/);
            const thumbnailUrl = thumbnailUrlMatch && thumbnailUrlMatch[1] ? thumbnailUrlMatch[1] : null;

            if (thumbnailUrl) return (
                <VideoCallout videoUrl={uriMatch[1]} title={text} thumbnailUrl={thumbnailUrl} />
            );

            if (thumbnailFile) return (
                <VideoCallout videoUrl={uriMatch[1]} title={text} thumbnailFileName={thumbnailFile} />
            );

            return null;
        });

        // Render HTML around the shortcodes
        shortcodeRendered = shortcodeRendered.map(contentBlock => {
            if (typeof contentBlock === 'string') {
                return (<div dangerouslySetInnerHTML={{__html: contentBlock}}/>);
            }
            return contentBlock;
        });

        return shortcodeRendered;
    }

    // Return standard format if shortcodes are not enabled, allows use of dangerouslySetInnerHTML
    return contentReplaced;
};

/**
 * Twill-specific filterHTML function to process with htmlReactParser in order to prevent breaking DOM structure during replace.
 *
 * @param content
 * @param shortcodesEnabled
 * @param filterAnchorClasses
 * @param filterAnchorInlineStyles
 * @param injectShortcodeForVideoLinks
 * @returns {string|JSX.Element}
 */
export const filterHTMLTwill = (content, shortcodesEnabled=false, filterAnchorClasses=true, filterAnchorInlineStyles=true, injectShortcodeForVideoLinks=true) =>
    htmlReactParser(content.replace(/®|&reg;/g, '<sup>&reg;</sup>'), {
        replace(domNode) {
            // Isolate likely shortcodes via domNodes that include both [[ and ]]
            if (domNode && domNode.data && typeof(domNode.data) === 'string' && (domNode.data.includes('[[') && domNode.data.includes(']]'))) {
                return (<React.Fragment>{filterHTML(domNode.data, shortcodesEnabled, filterAnchorClasses, filterAnchorInlineStyles, injectShortcodeForVideoLinks)}</React.Fragment>);
            }

            return domNode;
        }
    });




export const accentFold = (s) => {
    if (!s) { return ''; }

    const accentMap = {
        'ẚ':'a','Á':'a','á':'a','À':'a','à':'a','Ă':'a','ă':'a','Ắ':'a','ắ':'a','Ằ':'a','ằ':'a','Ẵ':'a','ẵ':'a','Ẳ':'a','ẳ':'a','Â':'a','â':'a','Ấ':'a','ấ':'a','Ầ':'a','ầ':'a','Ẫ':'a','ẫ':'a','Ẩ':'a','ẩ':'a','Ǎ':'a','ǎ':'a','Å':'a','å':'a','Ǻ':'a','ǻ':'a','Ä':'a','ä':'a','Ǟ':'a','ǟ':'a','Ã':'a','ã':'a','Ȧ':'a','ȧ':'a','Ǡ':'a','ǡ':'a','Ą':'a','ą':'a','Ā':'a','ā':'a','Ả':'a','ả':'a','Ȁ':'a','ȁ':'a','Ȃ':'a','ȃ':'a','Ạ':'a','ạ':'a','Ặ':'a','ặ':'a','Ậ':'a','ậ':'a','Ḁ':'a','ḁ':'a','Ⱥ':'a','ⱥ':'a','Ǽ':'a','ǽ':'a','Ǣ':'a','ǣ':'a',
        'Ḃ':'b','ḃ':'b','Ḅ':'b','ḅ':'b','Ḇ':'b','ḇ':'b','Ƀ':'b','ƀ':'b','ᵬ':'b','Ɓ':'b','ɓ':'b','Ƃ':'b','ƃ':'b',
        'Ć':'c','ć':'c','Ĉ':'c','ĉ':'c','Č':'c','č':'c','Ċ':'c','ċ':'c','Ç':'c','ç':'c','Ḉ':'c','ḉ':'c','Ȼ':'c','ȼ':'c','Ƈ':'c','ƈ':'c','ɕ':'c',
        'Ď':'d','ď':'d','Ḋ':'d','ḋ':'d','Ḑ':'d','ḑ':'d','Ḍ':'d','ḍ':'d','Ḓ':'d','ḓ':'d','Ḏ':'d','ḏ':'d','Đ':'d','đ':'d','ᵭ':'d','Ɖ':'d','ɖ':'d','Ɗ':'d','ɗ':'d','Ƌ':'d','ƌ':'d','ȡ':'d','ð':'d',
        'É':'e','Ə':'e','Ǝ':'e','ǝ':'e','é':'e','È':'e','è':'e','Ĕ':'e','ĕ':'e','Ê':'e','ê':'e','Ế':'e','ế':'e','Ề':'e','ề':'e','Ễ':'e','ễ':'e','Ể':'e','ể':'e','Ě':'e','ě':'e','Ë':'e','ë':'e','Ẽ':'e','ẽ':'e','Ė':'e','ė':'e','Ȩ':'e','ȩ':'e','Ḝ':'e','ḝ':'e','Ę':'e','ę':'e','Ē':'e','ē':'e','Ḗ':'e','ḗ':'e','Ḕ':'e','ḕ':'e','Ẻ':'e','ẻ':'e','Ȅ':'e','ȅ':'e','Ȇ':'e','ȇ':'e','Ẹ':'e','ẹ':'e','Ệ':'e','ệ':'e','Ḙ':'e','ḙ':'e','Ḛ':'e','ḛ':'e','Ɇ':'e','ɇ':'e','ɚ':'e','ɝ':'e',
        'Ḟ':'f','ḟ':'f','ᵮ':'f','Ƒ':'f','ƒ':'f',
        'Ǵ':'g','ǵ':'g','Ğ':'g','ğ':'g','Ĝ':'g','ĝ':'g','Ǧ':'g','ǧ':'g','Ġ':'g','ġ':'g','Ģ':'g','ģ':'g','Ḡ':'g','ḡ':'g','Ǥ':'g','ǥ':'g','Ɠ':'g','ɠ':'g',
        'Ĥ':'h','ĥ':'h','Ȟ':'h','ȟ':'h','Ḧ':'h','ḧ':'h','Ḣ':'h','ḣ':'h','Ḩ':'h','ḩ':'h','Ḥ':'h','ḥ':'h','Ḫ':'h','ḫ':'h','H':'h','̱':'h','ẖ':'h','Ħ':'h','ħ':'h','Ⱨ':'h','ⱨ':'h',
        'Í':'i','í':'i','Ì':'i','ì':'i','Ĭ':'i','ĭ':'i','Î':'i','î':'i','Ǐ':'i','ǐ':'i','Ï':'i','ï':'i','Ḯ':'i','ḯ':'i','Ĩ':'i','ĩ':'i','İ':'i','i':'i','Į':'i','į':'i','Ī':'i','ī':'i','Ỉ':'i','ỉ':'i','Ȉ':'i','ȉ':'i','Ȋ':'i','ȋ':'i','Ị':'i','ị':'i','Ḭ':'i','ḭ':'i','I':'i','ı':'i','Ɨ':'i','ɨ':'i',
        'Ĵ':'j','ĵ':'j','J':'j','̌':'j','ǰ':'j','ȷ':'j','Ɉ':'j','ɉ':'j','ʝ':'j','ɟ':'j','ʄ':'j',
        'Ḱ':'k','ḱ':'k','Ǩ':'k','ǩ':'k','Ķ':'k','ķ':'k','Ḳ':'k','ḳ':'k','Ḵ':'k','ḵ':'k','Ƙ':'k','ƙ':'k','Ⱪ':'k','ⱪ':'k',
        'Ĺ':'l','ĺ':'l','Ľ':'l','ľ':'l','Ļ':'l','ļ':'l','Ḷ':'l','ḷ':'l','Ḹ':'l','ḹ':'l','Ḽ':'l','ḽ':'l','Ḻ':'l','ḻ':'l','Ł':'l','ł':'l','Ł':'l','̣':'l','ł':'l','̣':'l','Ŀ':'l','ŀ':'l','Ƚ':'l','ƚ':'l','Ⱡ':'l','ⱡ':'l','Ɫ':'l','ɫ':'l','ɬ':'l','ɭ':'l','ȴ':'l',
        'Ḿ':'m','ḿ':'m','Ṁ':'m','ṁ':'m','Ṃ':'m','ṃ':'m','ɱ':'m',
        'Ń':'n','ń':'n','Ǹ':'n','ǹ':'n','Ň':'n','ň':'n','Ñ':'n','ñ':'n','Ṅ':'n','ṅ':'n','Ņ':'n','ņ':'n','Ṇ':'n','ṇ':'n','Ṋ':'n','ṋ':'n','Ṉ':'n','ṉ':'n','Ɲ':'n','ɲ':'n','Ƞ':'n','ƞ':'n','ɳ':'n','ȵ':'n','N':'n','̈':'n','n':'n','̈':'n',
        'Ó':'o','ó':'o','Ò':'o','ò':'o','Ŏ':'o','ŏ':'o','Ô':'o','ô':'o','Ố':'o','ố':'o','Ồ':'o','ồ':'o','Ỗ':'o','ỗ':'o','Ổ':'o','ổ':'o','Ǒ':'o','ǒ':'o','Ö':'o','ö':'o','Ȫ':'o','ȫ':'o','Ő':'o','ő':'o','Õ':'o','õ':'o','Ṍ':'o','ṍ':'o','Ṏ':'o','ṏ':'o','Ȭ':'o','ȭ':'o','Ȯ':'o','ȯ':'o','Ȱ':'o','ȱ':'o','Ø':'o','ø':'o','Ǿ':'o','ǿ':'o','Ǫ':'o','ǫ':'o','Ǭ':'o','ǭ':'o','Ō':'o','ō':'o','Ṓ':'o','ṓ':'o','Ṑ':'o','ṑ':'o','Ỏ':'o','ỏ':'o','Ȍ':'o','ȍ':'o','Ȏ':'o','ȏ':'o','Ơ':'o','ơ':'o','Ớ':'o','ớ':'o','Ờ':'o','ờ':'o','Ỡ':'o','ỡ':'o','Ở':'o','ở':'o','Ợ':'o','ợ':'o','Ọ':'o','ọ':'o','Ộ':'o','ộ':'o','Ɵ':'o','ɵ':'o',
        'Ṕ':'p','ṕ':'p','Ṗ':'p','ṗ':'p','Ᵽ':'p','Ƥ':'p','ƥ':'p','P':'p','̃':'p','p':'p','̃':'p',
        'ʠ':'q','Ɋ':'q','ɋ':'q',
        'Ŕ':'r','ŕ':'r','Ř':'r','ř':'r','Ṙ':'r','ṙ':'r','Ŗ':'r','ŗ':'r','Ȑ':'r','ȑ':'r','Ȓ':'r','ȓ':'r','Ṛ':'r','ṛ':'r','Ṝ':'r','ṝ':'r','Ṟ':'r','ṟ':'r','Ɍ':'r','ɍ':'r','ᵲ':'r','ɼ':'r','Ɽ':'r','ɽ':'r','ɾ':'r','ᵳ':'r',
        'ß':'s','Ś':'s','ś':'s','Ṥ':'s','ṥ':'s','Ŝ':'s','ŝ':'s','Š':'s','š':'s','Ṧ':'s','ṧ':'s','Ṡ':'s','ṡ':'s','ẛ':'s','Ş':'s','ş':'s','Ṣ':'s','ṣ':'s','Ṩ':'s','ṩ':'s','Ș':'s','ș':'s','ʂ':'s','S':'s','̩':'s','s':'s','̩':'s',
        'Þ':'t','þ':'t','Ť':'t','ť':'t','T':'t','̈':'t','ẗ':'t','Ṫ':'t','ṫ':'t','Ţ':'t','ţ':'t','Ṭ':'t','ṭ':'t','Ț':'t','ț':'t','Ṱ':'t','ṱ':'t','Ṯ':'t','ṯ':'t','Ŧ':'t','ŧ':'t','Ⱦ':'t','ⱦ':'t','ᵵ':'t','ƫ':'t','Ƭ':'t','ƭ':'t','Ʈ':'t','ʈ':'t','ȶ':'t',
        'Ú':'u','ú':'u','Ù':'u','ù':'u','Ŭ':'u','ŭ':'u','Û':'u','û':'u','Ǔ':'u','ǔ':'u','Ů':'u','ů':'u','Ü':'u','ü':'u','Ǘ':'u','ǘ':'u','Ǜ':'u','ǜ':'u','Ǚ':'u','ǚ':'u','Ǖ':'u','ǖ':'u','Ű':'u','ű':'u','Ũ':'u','ũ':'u','Ṹ':'u','ṹ':'u','Ų':'u','ų':'u','Ū':'u','ū':'u','Ṻ':'u','ṻ':'u','Ủ':'u','ủ':'u','Ȕ':'u','ȕ':'u','Ȗ':'u','ȗ':'u','Ư':'u','ư':'u','Ứ':'u','ứ':'u','Ừ':'u','ừ':'u','Ữ':'u','ữ':'u','Ử':'u','ử':'u','Ự':'u','ự':'u','Ụ':'u','ụ':'u','Ṳ':'u','ṳ':'u','Ṷ':'u','ṷ':'u','Ṵ':'u','ṵ':'u','Ʉ':'u','ʉ':'u',
        'Ṽ':'v','ṽ':'v','Ṿ':'v','ṿ':'v','Ʋ':'v','ʋ':'v',
        'Ẃ':'w','ẃ':'w','Ẁ':'w','ẁ':'w','Ŵ':'w','ŵ':'w','W':'w','̊':'w','ẘ':'w','Ẅ':'w','ẅ':'w','Ẇ':'w','ẇ':'w','Ẉ':'w','ẉ':'w',
        'Ẍ':'x','ẍ':'x','Ẋ':'x','ẋ':'x',
        'Ý':'y','ý':'y','Ỳ':'y','ỳ':'y','Ŷ':'y','ŷ':'y','Y':'y','̊':'y','ẙ':'y','Ÿ':'y','ÿ':'y','Ỹ':'y','ỹ':'y','Ẏ':'y','ẏ':'y','Ȳ':'y','ȳ':'y','Ỷ':'y','ỷ':'y','Ỵ':'y','ỵ':'y','ʏ':'y','Ɏ':'y','ɏ':'y','Ƴ':'y','ƴ':'y',
        'Ź':'z','ź':'z','Ẑ':'z','ẑ':'z','Ž':'z','ž':'z','Ż':'z','ż':'z','Ẓ':'z','ẓ':'z','Ẕ':'z','ẕ':'z','Ƶ':'z','ƶ':'z','Ȥ':'z','ȥ':'z','ʐ':'z','ʑ':'z','Ⱬ':'z','ⱬ':'z','Ǯ':'z','ǯ':'z','ƺ':'z'
    };

    let ret = '';
    for (let i=0; i<s.length; i+=1) {
        ret += accentMap[s.charAt(i)] || s.charAt(i);
    }
    return ret;
};

export const stripTrailingSlash = url => {
    if(url.substr(-1) === '/') {
        return url.substr(0, url.length - 1);
    }
    return url;
};



// WEBPACK FOOTER //
// ./frontend/helpers/filters.js